// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT.
//
// Copied from shared/consent-ui/ConsentProvider.jsx by shared/sync-consent-ui.js.
// Edit the original and re-run the sync; changes made here are overwritten and
// will silently un-share this component from the other NexusAI apps.
// ─────────────────────────────────────────────────────────────────────────────

import { createContext, useContext, useMemo } from 'react';

/**
 * ConsentProvider — the one place each app plugs its own wiring into the shared
 * consent UI.
 *
 * These components used to be copy-pasted into every frontend, which meant a fix
 * had to be made three times and the copies had already begun to drift. They
 * couldn't simply be shared as-is because the apps genuinely differ in two ways:
 *
 *   api    — each frontend has its own axios client, with its own base URL, auth
 *            header convention and response-unwrapping interceptor. The shared
 *            components must never import an app's client directly, so the
 *            calls are injected here instead.
 *
 *   theme  — SkillNexus uses `bg-surface-1`, InternNexus `bg-card`, PromptNexus
 *            raw palette classes. Rather than force one of those on everyone (or
 *            fork the component again), each app supplies its own class names and
 *            the components stay presentation-agnostic.
 *
 * Everything is optional: the defaults below are plain Tailwind palette classes
 * that render correctly in an app with no design tokens at all.
 */

const ConsentContext = createContext(null);

// Neutral defaults — deliberately not tied to any app's token system.
export const DEFAULT_THEME = {
  surface:      'bg-white dark:bg-neutral-900',
  border:       'border-neutral-200 dark:border-neutral-800',
  borderHover:  'hover:border-neutral-300 dark:hover:border-neutral-700',
  text:         'text-neutral-900 dark:text-neutral-100',
  textMuted:    'text-neutral-600 dark:text-neutral-400',
  textFaint:    'text-neutral-500 dark:text-neutral-500',
  accent:       'text-emerald-700 dark:text-emerald-400',
  accentBg:     'bg-emerald-100 dark:bg-emerald-900/40',
  accentButton: 'bg-emerald-600 hover:bg-emerald-700',
  checkbox:     'accent-emerald-600',
  ghostHover:   'hover:bg-neutral-100 dark:hover:bg-neutral-800',
  errorBox:     'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  privacyUrl:   'https://nexusaiweb.com/privacy',
};

export function ConsentProvider({ api = {}, theme, children }) {
  const value = useMemo(
    () => ({ api, theme: { ...DEFAULT_THEME, ...(theme || {}) } }),
    [api, theme],
  );
  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

// Falls back to defaults rather than throwing when no provider is mounted, so a
// component dropped into a page before the app is wired up still renders — it
// just can't perform the API calls, which fail loudly at the point of use.
export function useConsentConfig() {
  return useContext(ConsentContext) || { api: {}, theme: DEFAULT_THEME };
}

export default ConsentProvider;
